import { Injectable, OnDestroy, inject, signal, computed } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Auth } from '@angular/fire/auth';
import {
  ConnectionStatus,
  VeoSSEEventType,
  VeoSSEConnectedEvent,
  VeoDeviceStatusEvent,
  VeoLocationUpdateEvent,
  VeoRouteAssignedEvent,
  VeoDashboardRefreshEvent,
  AlertEvent,
  NotificationSeenEvent,
} from '../models/sse-events.model';

/**
 * VEO SSE Service
 *
 * Organization-scoped SSE connection for VEO Dashboard real-time updates.
 * Connects to /api/v1/veo/events and automatically receives events filtered
 * by the user's organization access (from Firebase custom claims).
 *
 * Features:
 * - Organization-filtered event streams (multi-tenant)
 * - Automatic reconnection with exponential backoff
 * - Connection health monitoring (state tracking)
 * - Keep-alive handling (30s server pings)
 * - Typed observables for each VEO event type
 * - Handles graceful disconnection and cleanup
 *
 * Event Types:
 *   device_status     - Device online/offline/warning changes
 *   location_update   - Location metadata/assignment changes
 *   route_assigned    - Route assignment changes
 *   dashboard_refresh - Dashboard KPI data changed
 */
@Injectable({
  providedIn: 'root',
})
export class VeoSseService implements OnDestroy {
  private auth = inject(Auth);

  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  // VEO event streams
  private deviceStatus$ = new Subject<VeoDeviceStatusEvent>();
  private locationUpdate$ = new Subject<VeoLocationUpdateEvent>();
  private routeAssigned$ = new Subject<VeoRouteAssignedEvent>();
  private dashboardRefresh$ = new Subject<VeoDashboardRefreshEvent>();
  private veoNotification$ = new Subject<AlertEvent>();
  private veoNotificationSeen$ = new Subject<NotificationSeenEvent>();

  // Connection state
  private connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');
  private connectionError$ = new Subject<Error>();

  // Health metrics
  private connectedSince = signal<Date | null>(null);
  private totalEventsReceived = signal(0);
  private lastEventAt = signal<Date | null>(null);

  // Organization info received on connect
  readonly connectedOrganizations = signal<number[]>([]);

  // Public computed signals
  readonly isConnected = computed(() => this.connectionStatus$.value === 'connected');
  readonly isConnecting = computed(() => this.connectionStatus$.value === 'connecting');
  readonly uptimeSeconds = computed(() => {
    if (!this.connectedSince()) return 0;
    return Math.floor((Date.now() - this.connectedSince()!.getTime()) / 1000);
  });
  readonly eventsReceived = computed(() => this.totalEventsReceived());
  readonly lastEventAge = computed(() => {
    if (!this.lastEventAt()) return null;
    return Math.floor((Date.now() - this.lastEventAt()!.getTime()) / 1000);
  });

  constructor() {
    console.log('[VeoSseService] Service initialized');
  }

  /**
   * Connect to the VEO SSE stream.
   * The backend filters events based on the user's organization claims.
   */
  async connect(): Promise<void> {
    if (this.eventSource) {
      console.log('[VeoSseService] Already connected');
      return;
    }

    this.connectionStatus$.next('connecting');
    console.log('[VeoSseService] Connection state: connecting');

    try {
      // Get auth token for backend authentication
      const token = await this.auth.currentUser?.getIdToken();

      // Build URL — the backend resolves orgs from Firebase custom claims
      const sseBaseUrl = environment.api.sseBaseUrl || environment.api.baseUrl;
      const baseUrl = `${sseBaseUrl}/api/v1/veo/events`;
      const url = token ? `${baseUrl}?token=${token}` : baseUrl;
      console.log('[VeoSseService] Connecting to:', baseUrl);

      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        console.log('[VeoSseService] Connection established');
        this.connectionStatus$.next('connected');
        this.connectedSince.set(new Date());
        this.reconnectAttempts = 0;
      };

      this.eventSource.onerror = (error) => {
        console.error('[VeoSseService] Connection error:', error);
        this.connectionStatus$.next('disconnected');
        this.handleConnectionError();
      };

      this.setupEventListeners();
    } catch (error) {
      console.error('[VeoSseService] Failed to create EventSource:', error);
      this.connectionError$.next(error as Error);
    }
  }

  /**
   * Set up typed event listeners for all VEO event types.
   */
  private setupEventListeners(): void {
    if (!this.eventSource) return;

    // Connected event (includes organization info)
    this.eventSource.addEventListener('connected', (event: MessageEvent) => {
      try {
        const data: VeoSSEConnectedEvent = JSON.parse(event.data);
        this.connectedOrganizations.set(data.organizations || []);
        console.log(
          `[VeoSseService] Connected for organizations:`,
          data.organizations
        );
      } catch (error) {
        console.error('[VeoSseService] Failed to parse connected event:', error);
      }
    });

    // Device status changes
    this.eventSource.addEventListener(
      VeoSSEEventType.DEVICE_STATUS,
      (event: MessageEvent) => {
        try {
          const data: VeoDeviceStatusEvent = JSON.parse(event.data);
          console.log('[VeoSseService] Device status:', data);
          this.totalEventsReceived.update((n) => n + 1);
          this.lastEventAt.set(new Date());
          this.deviceStatus$.next(data);
        } catch (error) {
          console.error(
            '[VeoSseService] Failed to parse device_status event:',
            error
          );
        }
      }
    );

    // Location updates
    this.eventSource.addEventListener(
      VeoSSEEventType.LOCATION_UPDATE,
      (event: MessageEvent) => {
        try {
          const data: VeoLocationUpdateEvent = JSON.parse(event.data);
          console.log('[VeoSseService] Location update:', data);
          this.totalEventsReceived.update((n) => n + 1);
          this.lastEventAt.set(new Date());
          this.locationUpdate$.next(data);
        } catch (error) {
          console.error(
            '[VeoSseService] Failed to parse location_update event:',
            error
          );
        }
      }
    );

    // Route assignments
    this.eventSource.addEventListener(
      VeoSSEEventType.ROUTE_ASSIGNED,
      (event: MessageEvent) => {
        try {
          const data: VeoRouteAssignedEvent = JSON.parse(event.data);
          console.log('[VeoSseService] Route assigned:', data);
          this.totalEventsReceived.update((n) => n + 1);
          this.lastEventAt.set(new Date());
          this.routeAssigned$.next(data);
        } catch (error) {
          console.error(
            '[VeoSseService] Failed to parse route_assigned event:',
            error
          );
        }
      }
    );

    // Dashboard refresh
    this.eventSource.addEventListener(
      VeoSSEEventType.DASHBOARD_REFRESH,
      (event: MessageEvent) => {
        try {
          const data: VeoDashboardRefreshEvent = JSON.parse(event.data);
          console.log('[VeoSseService] Dashboard refresh:', data);
          this.totalEventsReceived.update((n) => n + 1);
          this.lastEventAt.set(new Date());
          this.dashboardRefresh$.next(data);
        } catch (error) {
          console.error(
            '[VeoSseService] Failed to parse dashboard_refresh event:',
            error
          );
        }
      }
    );

    // Org-filtered notifications
    this.eventSource.addEventListener(
      VeoSSEEventType.VEO_NOTIFICATION,
      (event: MessageEvent) => {
        try {
          const data: AlertEvent = JSON.parse(event.data);
          console.log('[VeoSseService] VEO notification:', data);
          this.totalEventsReceived.update((n) => n + 1);
          this.lastEventAt.set(new Date());
          this.veoNotification$.next(data);
        } catch (error) {
          console.error(
            '[VeoSseService] Failed to parse veo_notification event:',
            error
          );
        }
      }
    );

    // Org-filtered notification seen
    this.eventSource.addEventListener(
      VeoSSEEventType.VEO_NOTIFICATION_SEEN,
      (event: MessageEvent) => {
        try {
          const data: NotificationSeenEvent = JSON.parse(event.data);
          console.log('[VeoSseService] VEO notification seen:', data);
          this.totalEventsReceived.update((n) => n + 1);
          this.lastEventAt.set(new Date());
          this.veoNotificationSeen$.next(data);
        } catch (error) {
          console.error(
            '[VeoSseService] Failed to parse veo_notification_seen event:',
            error
          );
        }
      }
    );
  }

  /**
   * Handle connection errors and attempt reconnection with exponential backoff.
   */
  private handleConnectionError(): void {
    this.disconnect();

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max 16s)
      const delay = Math.min(
        1000 * Math.pow(2, this.reconnectAttempts),
        16000
      );
      this.reconnectAttempts++;

      console.log(
        `[VeoSseService] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
      );

      this.reconnectTimeout = setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error('[VeoSseService] Max reconnection attempts reached');
      this.connectionStatus$.next('error');
      const error = new Error(
        'Connection lost. Unable to reconnect to real-time updates. Please refresh the page.'
      );
      this.connectionError$.next(error);
    }
  }

  /**
   * Disconnect from the VEO SSE stream.
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      console.log('[VeoSseService] Disconnecting');
      this.eventSource.close();
      this.eventSource = null;
      this.connectionStatus$.next('disconnected');
      this.connectedSince.set(null);
    }
  }

  // ─── Public Observables ─────────────────────────────────

  /** Device status change events (online/offline/warning) */
  getDeviceStatusUpdates(): Observable<VeoDeviceStatusEvent> {
    return this.deviceStatus$.asObservable();
  }

  /** Location metadata/assignment change events */
  getLocationUpdates(): Observable<VeoLocationUpdateEvent> {
    return this.locationUpdate$.asObservable();
  }

  /** Route assignment change events */
  getRouteAssignments(): Observable<VeoRouteAssignedEvent> {
    return this.routeAssigned$.asObservable();
  }

  /** Dashboard KPI refresh events */
  getDashboardRefresh(): Observable<VeoDashboardRefreshEvent> {
    return this.dashboardRefresh$.asObservable();
  }

  /** Org-filtered notification alerts */
  getVeoNotifications(): Observable<AlertEvent> {
    return this.veoNotification$.asObservable();
  }

  /** Org-filtered notification seen events */
  getVeoNotificationSeen(): Observable<NotificationSeenEvent> {
    return this.veoNotificationSeen$.asObservable();
  }

  /** Connection state stream */
  getConnectionState(): Observable<ConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  /** Current connection status (synchronous) */
  getCurrentStatus(): ConnectionStatus {
    return this.connectionStatus$.value;
  }

  /** Connection error stream */
  getConnectionErrors(): Observable<Error> {
    return this.connectionError$.asObservable();
  }

  /**
   * Clean up on service destruction.
   */
  ngOnDestroy(): void {
    this.disconnect();
    this.deviceStatus$.complete();
    this.locationUpdate$.complete();
    this.routeAssigned$.complete();
    this.dashboardRefresh$.complete();
    this.veoNotification$.complete();
    this.veoNotificationSeen$.complete();
    this.connectionStatus$.complete();
    this.connectionError$.complete();
  }
}